import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

type JsonRecord = Record<string, unknown>;

function isPlainObject(value: unknown): value is JsonRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof FormData);
}

@Injectable()
export class MultilingualJsonInterceptor implements HttpInterceptor {
  private readonly MULTILINGUAL_PREFIX = '_multilingual_original_';
  private readonly I18N_SUFFIX = 'I18n';

  private get currentLang(): string {
    return localStorage.getItem('lang') ?? 'en';
  }

  intercept(request: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    if (request.url.includes('/assets/i18n/')) {
      return next.handle(request);
    }

    request = request.clone({
      setHeaders: {
        'Accept-Language': this.currentLang,
      },
    });

    if (request.body !== null && request.body !== undefined) {
      const transformedBody = this.transformRequest(request.body);
      request = request.clone({ body: transformedBody });
    }

    if (request.responseType === 'blob') {
      return next.handle(request);
    }

    return next.handle(request).pipe(
      map(event => {
        if (event instanceof HttpResponse) {
          const transformedBody = this.transformResponse(event.body);
          return event.clone({ body: transformedBody });
        }
        return event;
      }),
    );
  }

  private transformResponse(data: unknown): unknown {
    if (data === null || data === undefined) return data;
    if (Array.isArray(data)) {
      return data.map(item => this.transformResponse(item));
    }
    if (!isPlainObject(data)) return data;

    const result: JsonRecord = { ...data };
    for (const [key, value] of Object.entries(data)) {
      if (this.isMultilingualField(key)) {
        const baseKey = key.slice(0, -this.I18N_SUFFIX.length);

        if (value === null || value === undefined) {
          result[baseKey] = '';
          delete result[key];
          continue;
        }

        if (typeof value === 'string') {
          if (!value.trim()) {
            result[baseKey] = '';
            delete result[key];
            continue;
          }

          try {
            const parsed: unknown = JSON.parse(value);
            if (isPlainObject(parsed)) {
              result[`${this.MULTILINGUAL_PREFIX}${baseKey}`] = value;
              const extractedValue = this.extractCurrentLanguageValue(parsed);
              result[baseKey] = extractedValue || '';
              delete result[key];
            } else {
              const coerced = String(parsed);
              result[baseKey] =
                parsed === null || parsed === undefined || parsed === false || parsed === 0 || coerced === '' ? value : coerced;
              delete result[key];
            }
          } catch {
            result[baseKey] = value;
            delete result[key];
          }
        }
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.transformResponse(value);
      }
    }
    return result;
  }

  private transformRequest(data: unknown): unknown {
    if (data === null || data === undefined) return data;
    if (Array.isArray(data)) {
      return data.map(item => this.transformRequest(item));
    }
    if (!isPlainObject(data)) return data;

    const result: JsonRecord = { ...data };
    for (const [key, value] of Object.entries(data)) {
      if (key.startsWith(this.MULTILINGUAL_PREFIX)) {
        const baseKey = key.slice(this.MULTILINGUAL_PREFIX.length);
        const i18nKey = `${baseKey}${this.I18N_SUFFIX}`;
        const rawOriginal = data[key];
        try {
          if (typeof rawOriginal !== 'string') {
            result[key] = value;
            continue;
          }
          const parsedOriginal: unknown = JSON.parse(rawOriginal);
          if (!isPlainObject(parsedOriginal)) {
            result[key] = value;
            continue;
          }
          const monolingualValue = data[baseKey];
          if (monolingualValue !== undefined) {
            const updated: JsonRecord = {
              ...parsedOriginal,
              [this.currentLang]: String(monolingualValue),
            };
            result[i18nKey] = JSON.stringify(updated);
            delete result[key];
            delete result[baseKey];
          }
        } catch {
          result[key] = value;
        }
      } else if (typeof value === 'object' && value !== null) {
        result[key] = this.transformRequest(value);
      }
    }
    return result;
  }

  private isMultilingualField(key: string): boolean {
    return key.endsWith(this.I18N_SUFFIX);
  }

  private extractCurrentLanguageValue(multiLangObj: JsonRecord): string {
    const fallbackOrder = [this.currentLang, 'fr', 'en', 'de', 'it'];
    const seen = new Set<string>();

    for (const lang of fallbackOrder) {
      if (seen.has(lang)) continue;
      seen.add(lang);
      const candidate = multiLangObj[lang];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    for (const lang of Object.keys(multiLangObj)) {
      if (seen.has(lang)) continue;
      const candidate = multiLangObj[lang];
      if (typeof candidate === 'string' && candidate.trim()) {
        return candidate;
      }
    }

    return '';
  }
}
